import { useState, useEffect } from 'react';
import api from '@/services/api';

export interface BaseDocument {
  document_id: number;
  filename: string;
  role: string;
  upload_order?: number;
}

export type WorkflowStep = 'upload' | 'analyze' | 'complete';

export interface DiffViewData {
  templateDocId: number;
  generatedDocId: number;
  changes: any[];
  summary: {
    total_changes: number;
    high_confidence: number;
    needs_review: number;
  };
}

export type ModuleType = 'iso' | 'soc2' | 'esg' | 'questionnaire' | 'rfp';

interface UseDocumentWorkflowConfig {
  module: ModuleType;
  projectId: string;
  onInitialize?: () => Promise<void>;
}

export function useDocumentWorkflow<T extends BaseDocument>(config: UseDocumentWorkflowConfig) {
  const { module, projectId, onInitialize } = config;

  // Common state
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('upload');
  const [documents, setDocuments] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisPhase, setAnalysisPhase] = useState<'template' | 'extraction' | 'complete'>('template');
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [diffViewData, setDiffViewData] = useState<DiffViewData | null>(null);

  // Build API endpoints based on module
  const getEndpoint = (path: string) => `/${module}/projects/${projectId}${path}`;

  // Load documents
  const loadDocuments = async () => {
    try {
      const response = await api.get(getEndpoint('/documents'));
      setDocuments(response.data);
    } catch (error) {
      console.error(`Failed to load ${module} documents:`, error);
    }
  };

  // Initialize project and load documents on mount
  useEffect(() => {
    if (projectId) {
      if (onInitialize) {
        onInitialize();
      }
      loadDocuments();
    }
  }, [projectId]);

  // Upload reference documents
  const handleUploadReference = async (files: FileList) => {
    setLoading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('file', files[i]);
        
        await api.post(
          getEndpoint('/reference-documents'),
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
      }
      
      console.log(`Uploaded ${files.length} reference documents`);
      await loadDocuments();
    } catch (error: any) {
      console.error(`Failed to upload ${module} reference documents:`, error);
    } finally {
      setLoading(false);
    }
  };

  // Upload template document
  const handleUploadTemplate = async (file: File | null) => {
    if (!file) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(
        getEndpoint('/template'),
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      console.log('Template uploaded successfully');
      await loadDocuments();
      if (onInitialize) {
        await onInitialize();
      }
    } catch (error: any) {
      console.error(`Failed to upload ${module} template:`, error);
    } finally {
      setLoading(false);
    }
  };

  // Analyze and fill template
  const handleAnalyzeAndFill = async () => {
    setAnalyzing(true);
    setCurrentStep('analyze');
    setAnalysisPhase('template');
    const startTimeValue = Date.now();
    
    try {
      setAnalysisPhase('extraction');
      
      // Get template and reference documents
      const templateDocument = documents.find(d => d.role === 'template');
      const referenceDocuments = documents.filter(d => d.role === 'reference');
      
      // Call backend to generate document dynamically
      if (templateDocument && referenceDocuments.length > 0) {
        try {
          const response = await api.post(getEndpoint('/generate'));
          
          setDiffViewData({
            templateDocId: response.data.template_document_id,
            generatedDocId: response.data.generated_document_id,
            changes: response.data.changes,
            summary: response.data.summary
          });
          
          // Set complete AFTER generation finishes
          setAnalysisPhase('complete');
          const elapsed = Math.round((Date.now() - startTimeValue) / 1000);
          setElapsedTime(elapsed);
        } catch (error: any) {
          console.error('Failed to generate document:', error);
          alert(`Document generation failed: ${error.response?.data?.detail || error.message}`);
          throw error; // Re-throw to trigger outer catch
        }
      } else {
        console.warn('Missing template or reference documents for generation');
      }
      
      setCurrentStep('complete');
    } catch (error: any) {
      console.error('Analysis failed:', error);
      setCurrentStep('upload');
    } finally {
      setAnalyzing(false);
    }
  };

  // Delete document
  const handleDeleteDocument = async (documentId: number) => {
    try {
      await api.delete(getEndpoint(`/documents/${documentId}`));
      console.log('Document deleted successfully');
      await loadDocuments();
    } catch (error: any) {
      console.error(`Failed to delete ${module} document:`, error);
    }
  };

  // Reset to upload view
  const handleBackToUpload = () => {
    setDiffViewData(null);
    setCurrentStep('upload');
    setAnalyzing(false);
    setAnalysisPhase('template');
    setElapsedTime(0);
  };

  // Computed values
  const templateDocument = documents.find(d => d.role === 'template');
  const referenceDocuments = documents.filter(d => d.role === 'reference');

  return {
    // State
    currentStep,
    documents,
    loading,
    analyzing,
    analysisPhase,
    elapsedTime,
    selectedDocuments,
    diffViewData,
    templateDocument,
    referenceDocuments,
    
    // Setters (for custom use cases)
    setCurrentStep,
    setDocuments,
    setLoading,
    setAnalyzing,
    setAnalysisPhase,
    setElapsedTime,
    setSelectedDocuments,
    setDiffViewData,
    
    // Actions
    loadDocuments,
    handleUploadReference,
    handleUploadTemplate,
    handleAnalyzeAndFill,
    handleDeleteDocument,
    handleBackToUpload,
  };
}
